import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
  CreatePolicyRenewal,
  SelectPolicy,
  UpdatePolicyRenewal,
  CreatePolicyRenewalWithAttachments,
  ResetRenewal,
  FinishPendingRenewal,
} from 'actions';
import { TRACKING_EVENTS, trackEventWithData } from 'helpers';
import { IAppState, IPolicy, IPolicyRenewal } from 'types';
import { ICreatePolicyRenewalProps, IFile } from 'services';
import styles from './styles/renewalUpdate.scss';
import RenewalUpload from './pages/RenewalUpload';
import RenewalManual from './pages/RenewalManual';
import { HOME_ROUTE } from 'constants/routes';
import Loading from 'components/loading';
import RouterPrompt from 'components/router-prompt';

export enum RenewalUpdatePaths {
  UPLOAD = 'UPLOAD',
  MANUAL = 'MANUAL',
  END = 'END',
}

interface IProps {
  policy: IPolicy;
  defaultPath?: RenewalUpdatePaths;
  prevButtonText?: string;
  showMarketScanBtn?: boolean;
  onPrevBtnClick?(): void;
}

interface IDispatchProps {
  resetPolicyForm(): void;
  selectPolicy(policy: IPolicy): void;
  createPolicyRenewal(
    renewal: ICreatePolicyRenewalProps,
    policy: IPolicy,
  ): void;
  createPolicyRenewalWithAttachments(policy: IPolicy, files: IFile[]): void;
  updatePolicyRenewal(renewal: IPolicyRenewal): void;
  finishPendingRenewal(renewal: IPolicyRenewal): void;
}

const pathMap = {
  [RenewalUpdatePaths.UPLOAD]: RenewalUpload,
  [RenewalUpdatePaths.MANUAL]: RenewalManual,
};

const RenewalUpdate = ({
  defaultPath = RenewalUpdatePaths.UPLOAD,
  policy: selectedPolicy,
  policies: { loading },
  policyRenewal: { submitted, submitting },
  referenceData,
  prevButtonText = 'Back to dashboard',
  showMarketScanBtn,
  onPrevBtnClick,
  createPolicyRenewalWithAttachments,
  resetPolicyForm,
}: IProps & IAppState & IDispatchProps) => {
  useEffect(resetPolicyForm, []);

  const [path, setPath] = useState(defaultPath);

  useEffect(() => {
    if (defaultPath !== path) {
      setPath(defaultPath);
    }
  }, [defaultPath]);

  if (loading) {
    return <Loading />;
  }

  if (R.isEmpty(selectedPolicy)) {
    return <Redirect to={HOME_ROUTE} />;
  }

  const PageComponent = pathMap[path];

  const goToUpload = () => {
    if (path !== RenewalUpdatePaths.UPLOAD) {
      trackEventWithData(
        TRACKING_EVENTS.PolicyRenewalUploadTabClick,
        selectedPolicy,
      );
      setPath(RenewalUpdatePaths.UPLOAD);
    }
  };

  const goToManual = () => {
    if (path !== RenewalUpdatePaths.MANUAL) {
      trackEventWithData(
        TRACKING_EVENTS.PolicyRenewalManualTabClick,
        selectedPolicy,
      );
      setPath(RenewalUpdatePaths.MANUAL);
    }
  };

  return (
    <div className={styles.container}>
      {(submitted || submitting) && (
        <RouterPrompt closeOnOutsideClick={true}>
          <b>
            We’re still scanning information from your document.
            <br />
            Are you sure you want to leave?
          </b>
        </RouterPrompt>
      )}
      <div className={styles.tabs}>
        <div
          onClick={goToUpload}
          className={classnames(
            styles.tab,
            path === RenewalUpdatePaths.UPLOAD && styles.active,
          )}
        >
          Upload your new renewal documents
        </div>
        <div
          onClick={goToManual}
          className={classnames(
            styles.tab,
            path === RenewalUpdatePaths.MANUAL && styles.active,
          )}
        >
          Enter renewal information manually
        </div>
      </div>
      <PageComponent
        policy={selectedPolicy}
        submitted={submitted}
        submitting={submitting}
        referenceData={referenceData}
        prevButtonText={prevButtonText}
        showMarketScanBtn={showMarketScanBtn}
        onPrevBtnClick={onPrevBtnClick}
        createPolicyRenewalWithAttachments={createPolicyRenewalWithAttachments}
        resetPolicyForm={resetPolicyForm}
      />
    </div>
  );
};

const mapStateToProps = (state: IAppState) => ({
  ...state,
});

const mapDispatchToProps = {
  createPolicyRenewal: CreatePolicyRenewal,
  createPolicyRenewalWithAttachments: CreatePolicyRenewalWithAttachments,
  finishPendingRenewal: FinishPendingRenewal,
  resetPolicyForm: ResetRenewal,
  selectPolicy: SelectPolicy,
  updatePolicyRenewal: UpdatePolicyRenewal,
};

export default connect<IAppState, IDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(RenewalUpdate);
